import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Trash2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/editor-fields';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatStoreTime, toApiDate } from '@/lib/store-time';
import { useDeleteSponsorship, useSaveSponsorship } from './sponsorships.api';
import type { SponsorshipInput } from './types';

/**
 * A sponsorship is a name and a window; the items and stores are attached on
 * its own page, which is why this drawer is three fields.
 */

const dayToApi = (value: string, endOfDay = false) =>
  value ? toApiDate(value, endOfDay) : null;

export function SponsorshipEditor({
  sponsorship,
  open,
  onOpenChange,
  onDeleted,
}: {
  sponsorship: SponsorshipInput | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The detail page uses this to leave after deleting what it was showing. */
  onDeleted?: () => void;
}) {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');

  const save = useSaveSponsorship();
  const remove = useDeleteSponsorship();

  useEffect(() => {
    setName(sponsorship?.name ?? '');
    setStart(
      sponsorship?.startDate ? formatStoreTime(sponsorship.startDate, true) : ''
    );
    setEnd(
      sponsorship?.endDate ? formatStoreTime(sponsorship.endDate, true) : ''
    );
  }, [sponsorship]);

  const id = sponsorship?.id ?? null;
  const isEdit = !!id;
  const busy = save.isPending || remove.isPending;

  const startsAfterEnds =
    !!start && !!end && Date.parse(start) > Date.parse(end);
  const canSave = !!name.trim() && !!start && !!end && !startsAfterEnds;

  function submit() {
    save.mutate(
      {
        id,
        name: name.trim(),
        startDate: dayToApi(start),
        // Inclusive of the last day, which is how a reader reads a date range.
        endDate: dayToApi(end, true),
      },
      {
        onSuccess: () => {
          toast.success(
            isEdit ? t('sponsorships.updated') : t('sponsorships.created')
          );
          onOpenChange(false);
        },
        onError: (error) =>
          toast.error(
            error instanceof Error ? error.message : t('sponsorships.saveFailed')
          ),
      }
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
      >
        <SheetHeader className="flex-row items-center justify-between gap-3 border-b pr-12">
          <SheetTitle>
            {isEdit
              ? t('sponsorships.editSponsorship')
              : t('sponsorships.newSponsorship')}
          </SheetTitle>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  disabled={busy}
                >
                  <Trash2 className="size-3.5" />
                  {t('sponsorships.delete')}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>
                    {t('sponsorships.delete')}
                  </AlertDialogTitle>
                  <AlertDialogDescription>
                    {t('sponsorships.deleteConfirm', { name })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() =>
                      remove.mutate(id!, {
                        onSuccess: () => {
                          toast.success(t('sponsorships.deleted'));
                          onOpenChange(false);
                          onDeleted?.();
                        },
                        onError: () =>
                          toast.error(t('sponsorships.saveFailed')),
                      })
                    }
                  >
                    {t('sponsorships.delete')}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </SheetHeader>

        <div className="space-y-4 p-4">
          <Field
            label={t('sponsorships.name')}
            htmlFor="name"
            hint={t('sponsorships.nameHint')}
          >
            <Input
              id="name"
              value={name}
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('sponsorships.starts')} htmlFor="start">
              <Input
                id="start"
                type="date"
                value={start}
                disabled={busy}
                onChange={(event) => setStart(event.target.value)}
              />
            </Field>
            <Field label={t('sponsorships.ends')} htmlFor="end">
              <Input
                id="end"
                type="date"
                value={end}
                disabled={busy}
                onChange={(event) => setEnd(event.target.value)}
              />
            </Field>
          </div>

          {startsAfterEnds && (
            <p className="text-destructive text-xs">
              {t('sponsorships.endBeforeStart')}
            </p>
          )}

          <p className="text-muted-foreground text-xs">
            {t('sponsorships.windowHint')}
          </p>

          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button disabled={!canSave || busy} onClick={submit}>
              {save.isPending && <Loader2 className="animate-spin" />}
              {isEdit ? t('common.save') : t('sponsorships.create')}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
